import type {
  ActivityDto,
  AttachmentDto,
  BoardArchiveDto,
  BoardDto,
  BoardSummary,
  CardDto,
  ChecklistItemDto,
  ColumnDto,
  CommentDto,
  LabelColor,
  LabelDto,
  NudgeResult,
  RecurrenceDto,
  RecurrenceInput,
  ReminderDto,
  SavedViewDto,
  UserSummary,
} from '@kanban/shared';
import { startOfTodayEndOfDay } from '@kanban/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api, upload } from './api.js';
import { applyDrop, type DropTarget } from './drag-position.js';
import { formatTime } from './format.js';
import { useCurrentUser } from './session.js';
import { useToast } from './toast.js';

export const boardKey = (boardId: string) => ['board', boardId] as const;
export const boardsKey = ['boards'] as const;
const archiveKey = (boardId: string) => ['board', boardId, 'archiv'] as const;

export function useBoards() {
  return useQuery({
    queryKey: boardsKey,
    queryFn: () => api.get<{ boards: BoardSummary[] }>('/boards').then((r) => r.boards),
  });
}

export function useBoard(boardId: string | undefined) {
  return useQuery({
    queryKey: boardKey(boardId ?? ''),
    queryFn: () => api.get<{ board: BoardDto }>(`/boards/${boardId}`).then((r) => r.board),
    enabled: Boolean(boardId),
  });
}

export function useUsers() {
  return useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<{ users: UserSummary[] }>('/users').then((r) => r.users),
    staleTime: 5 * 60_000,
    /*
     * Beim Einhaengen trotzdem nachfragen. Die Liste steht fuenf Minuten als
     * frisch da, und seit dem Offline-Cache uebersteht dieser Stand sogar
     * einen Reload — ein neu angelegtes Familienmitglied tauchte dann in den
     * Board-Einstellungen minutenlang nicht auf. Es sind vier Zeilen Daten;
     * die Nachfrage kostet weniger als die Verwirrung.
     */
    refetchOnMount: 'always',
  });
}

/** Board im Cache umschreiben — Basis aller optimistischen Updates. */
function patchBoardCache(
  board: BoardDto | undefined,
  update: (columns: ColumnDto[]) => ColumnDto[],
): BoardDto | undefined {
  if (!board) return board;
  return { ...board, columns: update(board.columns) };
}

export function useCreateCard(boardId: string) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const me = useCurrentUser();

  return useMutation({
    mutationFn: (input: { columnId: string; title: string }) =>
      api
        .post<{ card: CardDto }>(`/columns/${input.columnId}/cards`, { title: input.title })
        .then((r) => r.card),

    // Optimistisch: die Karte erscheint sofort. Ein Board, das bei jedem Klick
    // auf den Server wartet, fuehlt sich auf dem Smartphone kaputt an (SPEC 16).
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: boardKey(boardId) });
      const previous = queryClient.getQueryData<BoardDto>(boardKey(boardId));
      const optimistic: CardDto = {
        id: `optimistisch-${Date.now()}`,
        columnId: input.columnId,
        title: input.title,
        description: null,
        position: 'zzz',
        // Ohne eigene Angabe gilt eine Karte als heute faellig — derselbe
        // Rueckfall wie serverseitig, sonst blitzte sie kurz ohne Faelligkeit auf.
        dueAt: startOfTodayEndOfDay().toISOString(),
        dueHasTime: false,
        startAt: null,
        priority: 0,
        completedAt: null,
        // Verantwortlich ist per Default die anlegende Person (Server setzt
        // denselben Rueckfall) — sonst blitzte die Karte kurz ohne Avatar auf.
        assignees: [{ id: me.id, name: me.name, avatarColor: me.avatarColor }],
        labels: [],
        checklist: { done: 0, total: 0 },
        commentCount: 0,
        attachmentCount: 0,
        createdById: me.id,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        isPrivate: false,
      };

      queryClient.setQueryData<BoardDto>(boardKey(boardId), (board) =>
        patchBoardCache(board, (columns) =>
          columns.map((column) =>
            column.id === input.columnId
              ? { ...column, cards: [...column.cards, optimistic] }
              : column,
          ),
        ),
      );
      return { previous };
    },

    onError: (error, _input, context) => {
      queryClient.setQueryData(boardKey(boardId), context?.previous);
      toast(error instanceof ApiError ? error.message : 'Karte konnte nicht angelegt werden', 'error');
    },

    onSettled: () => queryClient.invalidateQueries({ queryKey: boardKey(boardId) }),
  });
}

export function useMoveCard(boardId: string) {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    /**
     * Der Client berechnet den Fractional Key selbst und schickt genau ihn mit
     * (SPEC 5.1): der Server schreibt eine Zeile, und das optimistische Update
     * traegt denselben Wert — beim anschliessenden Abgleich rutscht nichts nach.
     */
    mutationFn: (input: {
      cardId: string;
      columnId: string;
      target: DropTarget;
      position: string;
    }) =>
      api
        .post<{ card: CardDto }>(`/cards/${input.cardId}/move`, {
          columnId: input.columnId,
          position: input.position,
        })
        .then((r) => r.card),

    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: boardKey(boardId) });
      const previous = queryClient.getQueryData<BoardDto>(boardKey(boardId));

      queryClient.setQueryData<BoardDto>(boardKey(boardId), (board) =>
        patchBoardCache(board, (columns) =>
          applyDrop(columns, input.cardId, input.target, input.position),
        ),
      );
      return { previous };
    },

    onError: (_error, _input, context) => {
      // Karte springt zurueck, Nutzer erfaehrt warum (SPEC 8.2.7).
      queryClient.setQueryData(boardKey(boardId), context?.previous);
      toast('Verschieben fehlgeschlagen', 'error');
    },

    onSettled: () => queryClient.invalidateQueries({ queryKey: boardKey(boardId) }),
  });
}

/**
 * Verschieben auf ein anderes Board (SPEC-Ergaenzung). Bewusst nicht
 * optimistisch wie `useMoveCard`: die Karte verlaesst dabei das Board, und
 * welche Labels/Verantwortlichen der Server beim Wechsel kappt, weiss der
 * Client vorher nicht — ein Rateversuch waere hier eher irreführend als
 * hilfreich.
 */
export function useMoveCardToBoard(boardId: string) {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (input: { cardId: string; targetBoardId: string; columnId: string }) =>
      api
        .post<{ card: CardDto }>(`/cards/${input.cardId}/move-to-board`, {
          boardId: input.targetBoardId,
          columnId: input.columnId,
        })
        .then((r) => r.card),

    onSuccess: (_card, input) => {
      void queryClient.invalidateQueries({ queryKey: boardKey(boardId) });
      void queryClient.invalidateQueries({ queryKey: boardKey(input.targetBoardId) });
    },

    onError: (error) => {
      toast(
        error instanceof ApiError ? error.message : 'Verschieben auf das andere Board fehlgeschlagen',
        'error',
      );
    },
  });
}

export function usePatchCard(boardId: string) {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (input: { cardId: string; patch: Record<string, unknown>; updatedAt: string }) =>
      api
        .patch<{ card: CardDto }>(`/cards/${input.cardId}`, {
          ...input.patch,
          updatedAt: input.updatedAt,
        })
        .then((r) => r.card),

    onSuccess: () => queryClient.invalidateQueries({ queryKey: boardKey(boardId) }),

    onError: (error) => {
      // 409: jemand anderes war schneller. Serverstand uebernehmen und sagen,
      // was passiert ist (SPEC 7.2) — nicht stillschweigend ueberschreiben.
      if (error instanceof ApiError && error.status === 409) {
        toast('Die Karte wurde gerade von jemand anderem geaendert. Stand aktualisiert.', 'info');
        void queryClient.invalidateQueries({ queryKey: boardKey(boardId) });
        return;
      }
      toast(error instanceof ApiError ? error.message : 'Speichern fehlgeschlagen', 'error');
    },
  });
}

/** Archiviertes zurueckholen (Papierkorb, Spalten-Sheet, Archivansicht). */
export function useRestoreCard(boardId: string) {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (cardId: string) => api.post(`/cards/${cardId}/restore`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: boardKey(boardId) });
      void queryClient.invalidateQueries({ queryKey: archiveKey(boardId) });
    },
    onError: () => toast('Wiederherstellen fehlgeschlagen', 'error'),
  });
}

export function useRestoreColumn(boardId: string) {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (columnId: string) => api.post(`/columns/${columnId}/restore`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: boardKey(boardId) });
      void queryClient.invalidateQueries({ queryKey: archiveKey(boardId) });
    },
    onError: () => toast('Wiederherstellen fehlgeschlagen', 'error'),
  });
}

/**
 * Archivieren einer Karte — optimistisch, mit Ruecknahme statt Rueckfrage.
 *
 * Ein Fehlgriff beim Ziehen ist auf dem Smartphone der Normalfall. Ein
 * Bestaetigungsdialog wuerde dafuer jeden richtigen Griff bestrafen; der
 * Rueckgaengig-Knopf kostet nur den seltenen Fehlgriff ein Antippen.
 */
export function useArchiveCard(boardId: string) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const restore = useRestoreCard(boardId);

  return useMutation({
    mutationFn: (cardId: string) => api.delete(`/cards/${cardId}`),
    onMutate: async (cardId) => {
      await queryClient.cancelQueries({ queryKey: boardKey(boardId) });
      const previous = queryClient.getQueryData<BoardDto>(boardKey(boardId));
      const title = previous?.columns
        .flatMap((column) => column.cards)
        .find((card) => card.id === cardId)?.title;
      queryClient.setQueryData<BoardDto>(boardKey(boardId), (board) =>
        patchBoardCache(board, (columns) =>
          columns.map((column) => ({
            ...column,
            cards: column.cards.filter((card) => card.id !== cardId),
          })),
        ),
      );
      return { previous, title };
    },
    onSuccess: (_data, cardId, context) => {
      toast(context?.title ? `„${context.title}" archiviert` : 'Karte archiviert', 'info', {
        label: 'Rückgängig',
        onAction: () => restore.mutate(cardId),
      });
    },
    onError: (_error, _cardId, context) => {
      queryClient.setQueryData(boardKey(boardId), context?.previous);
      toast('Archivieren fehlgeschlagen', 'error');
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: boardKey(boardId) }),
  });
}

export function useAssignee(boardId: string) {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (input: { cardId: string; userId: string; assign: boolean }) =>
      input.assign
        ? api.post(`/cards/${input.cardId}/assignees`, { userId: input.userId })
        : api.delete(`/cards/${input.cardId}/assignees/${input.userId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: boardKey(boardId) }),
    onError: () => toast('Zuweisung fehlgeschlagen', 'error'),
  });
}

export function useCreateColumn(boardId: string) {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (title: string) => api.post(`/boards/${boardId}/columns`, { title }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: boardKey(boardId) }),
    onError: () => toast('Spalte konnte nicht angelegt werden', 'error'),
  });
}

/**
 * Board aendern: Name, Symbol, Teilnehmer (SPEC 5.2).
 *
 * `updatedAt` geht mit, wie bei der Karte — zwei Leute, die gleichzeitig
 * umbenennen, sollen nicht stillschweigend der Letzte gewinnen lassen. Bei
 * `409` traegt die Antwort den Serverstand; den zeigt das Sheet an, statt
 * darueber zu schreiben.
 */
export function usePatchBoard(boardId: string) {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (input: {
      patch: { title?: string; icon?: string | null; memberIds?: string[]; archived?: boolean };
      updatedAt: string;
    }) =>
      api
        .patch<{ board: BoardDto }>(`/boards/${boardId}`, { ...input.patch, updatedAt: input.updatedAt })
        .then((r) => r.board),
    onSuccess: (board) => {
      // Den frischen Stand direkt setzen: das Sheet steht offen und soll nicht
      // erst auf die naechste Abfrage warten.
      queryClient.setQueryData(boardKey(boardId), board);
      void queryClient.invalidateQueries({ queryKey: boardsKey });
    },
    onError: (error) => {
      if (error instanceof ApiError && error.status === 409) {
        void queryClient.invalidateQueries({ queryKey: boardKey(boardId) });
        toast(
          error.body.actorName
            ? `${error.body.actorName} war schneller — hier ist der neue Stand`
            : 'Jemand war schneller — hier ist der neue Stand',
          'error',
        );
        return;
      }
      toast(
        error instanceof ApiError ? error.message : 'Board konnte nicht geändert werden',
        'error',
      );
    },
  });
}

export function useCreateBoard() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (input: { title: string; memberIds: string[] }) =>
      api.post<{ board: BoardDto }>('/boards', input).then((r) => r.board),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: boardsKey }),
    onError: (error) =>
      toast(error instanceof ApiError ? error.message : 'Board konnte nicht angelegt werden', 'error'),
  });
}

/** Eigene Reihenfolge der Board-Liste (SPEC-Ergaenzung). Ganze Liste auf einmal. */
export function useReorderBoards() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (boardIds: string[]) => api.post('/boards/reorder', { boardIds }),
    onMutate: async (boardIds) => {
      await queryClient.cancelQueries({ queryKey: boardsKey });
      const previous = queryClient.getQueryData<BoardSummary[]>(boardsKey);
      const byId = new Map((previous ?? []).map((board) => [board.id, board]));
      queryClient.setQueryData<BoardSummary[]>(
        boardsKey,
        boardIds.map((id) => byId.get(id)).filter((board): board is BoardSummary => Boolean(board)),
      );
      return { previous };
    },
    onError: (_error, _boardIds, context) => {
      queryClient.setQueryData(boardsKey, context?.previous);
      toast('Umsortieren fehlgeschlagen', 'error');
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: boardsKey }),
  });
}

/**
 * Wiederherstellen eines geloeschten Boards (Rueckgaengig-Toast). Eigene
 * Mutation statt `usePatchBoard`: dort ist die Board-ID beim Erzeugen des
 * Hooks fest, hier wechselt sie je nach Zug in der Liste.
 */
export function useRestoreBoard() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (input: { boardId: string; updatedAt: string }) =>
      api.patch<{ board: BoardDto }>(`/boards/${input.boardId}`, {
        archived: false,
        updatedAt: input.updatedAt,
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: boardsKey }),
    onError: () => toast('Wiederherstellen fehlgeschlagen', 'error'),
  });
}

/**
 * Archivieren per Zug in den Papierkorb — optimistisch, mit Ruecknahme statt
 * Rueckfrage, wie beim Archivieren einer Karte. Der zweistufige Knopf in den
 * Board-Einstellungen bleibt davon unberuehrt: dort gibt es kein Undo, hier
 * schon — zwei etwas unterschiedliche Sicherungen fuer denselben Vorgang.
 */
export function useArchiveBoard() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const restore = useRestoreBoard();

  return useMutation({
    mutationFn: (input: { boardId: string; updatedAt: string }) =>
      api.patch<{ board: BoardDto }>(`/boards/${input.boardId}`, {
        archived: true,
        updatedAt: input.updatedAt,
      }),
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: boardsKey });
      const previous = queryClient.getQueryData<BoardSummary[]>(boardsKey);
      const title = previous?.find((board) => board.id === input.boardId)?.title;
      queryClient.setQueryData<BoardSummary[]>(boardsKey, (list) =>
        (list ?? []).filter((board) => board.id !== input.boardId),
      );
      return { previous, title };
    },
    onSuccess: (data, input, context) => {
      toast(context?.title ? `„${context.title}" gelöscht` : 'Board gelöscht', 'info', {
        label: 'Rückgängig',
        // Der Server hat `updatedAt` beim Archivieren schon weitergeschrieben
        // — die Ruecknahme muss GENAU diesen frischen Stand kennen, sonst
        // scheitert sie am Optimistic-Lock-Vergleich.
        onAction: () => restore.mutate({ boardId: input.boardId, updatedAt: data.board.updatedAt }),
      });
    },
    onError: (_error, _input, context) => {
      queryClient.setQueryData(boardsKey, context?.previous);
      toast('Löschen fehlgeschlagen', 'error');
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: boardsKey }),
  });
}


/** Kartendetail inklusive Checkliste — nur beim Oeffnen des Sheets geladen. */
export function useCardDetail(cardId: string | null) {
  return useQuery({
    queryKey: ['card', cardId ?? ''],
    queryFn: () =>
      api.get<{
        card: CardDto;
        checklist: ChecklistItemDto[];
        recurrence: RecurrenceDto | null;
        comments: CommentDto[];
        attachments: AttachmentDto[];
      }>(`/cards/${cardId}`),
    enabled: Boolean(cardId),
  });
}

/**
 * Kommentare (SPEC 9.1).
 *
 * Kein optimistisches Update: ein Beitrag, der kurz dasteht und dann
 * verschwindet, waere in einem Gespraech schlimmer als eine halbe Sekunde
 * Wartezeit. Beim Verschieben einer Karte ist das anders — dort zaehlt das
 * unmittelbare Gefuehl, hier der Inhalt.
 */
export function useComments(boardId: string, cardId: string | null) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const refresh = () => {
    if (cardId) void queryClient.invalidateQueries({ queryKey: ['card', cardId] });
    // Der Zaehler steht auf der Karte im Board.
    void queryClient.invalidateQueries({ queryKey: boardKey(boardId) });
    if (cardId) void queryClient.invalidateQueries({ queryKey: cardActivityKey(cardId) });
    void queryClient.invalidateQueries({ queryKey: boardActivityKey(boardId) });
  };

  const onError = (error: unknown, fallback: string) =>
    toast(error instanceof ApiError ? error.message : fallback, 'error');

  return {
    create: useMutation({
      mutationFn: (body: string) =>
        api.post<{ comment: CommentDto }>(`/cards/${cardId}/comments`, { body }),
      onSuccess: refresh,
      onError: (error) => onError(error, 'Kommentar konnte nicht gespeichert werden'),
    }),
    patch: useMutation({
      mutationFn: ({ commentId, body }: { commentId: string; body: string }) =>
        api.patch<{ comment: CommentDto }>(`/comments/${commentId}`, { body }),
      onSuccess: refresh,
      onError: (error) => onError(error, 'Kommentar konnte nicht geaendert werden'),
    }),
    remove: useMutation({
      mutationFn: (commentId: string) => api.delete(`/comments/${commentId}`),
      onSuccess: refresh,
      onError: (error) => onError(error, 'Kommentar konnte nicht geloescht werden'),
    }),
  };
}

export const cardActivityKey = (cardId: string) => ['card', cardId, 'verlauf'] as const;
export const boardActivityKey = (boardId: string) => ['board', boardId, 'verlauf'] as const;

/**
 * Anhaenge (SPEC 9.8).
 *
 * Wie bei den Kommentaren ohne optimistisches Update: vor der Antwort gibt es
 * weder Groesse noch Vorschau, ein vorweggenommener Eintrag waere also eine
 * leere Zeile, die gleich wieder anders aussieht.
 */
export function useAttachments(boardId: string, cardId: string) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['card', cardId] });
    // Die Bueroklammer mit der Zahl steht auf der Karte im Board.
    void queryClient.invalidateQueries({ queryKey: boardKey(boardId) });
    void queryClient.invalidateQueries({ queryKey: cardActivityKey(cardId) });
    void queryClient.invalidateQueries({ queryKey: boardActivityKey(boardId) });
  };

  return {
    add: useMutation({
      mutationFn: (file: File) =>
        upload<{ attachment: AttachmentDto }>(`/cards/${cardId}/attachments`, file),
      onSuccess: refresh,
      onError: (error) =>
        toast(
          error instanceof ApiError ? error.message : 'Anhang konnte nicht gespeichert werden',
          'error',
        ),
    }),
    remove: useMutation({
      mutationFn: (attachmentId: string) => api.delete(`/attachments/${attachmentId}`),
      onSuccess: refresh,
      onError: (error) =>
        toast(
          error instanceof ApiError ? error.message : 'Anhang konnte nicht geloescht werden',
          'error',
        ),
    }),
  };
}

/** Verlauf einer Karte — erst geladen, wenn jemand ihn aufklappt. */
export function useCardActivity(cardId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: cardActivityKey(cardId ?? ''),
    queryFn: () =>
      api.get<{ activity: ActivityDto[] }>(`/cards/${cardId}/activity`).then((r) => r.activity),
    enabled: Boolean(cardId) && enabled,
  });
}

export function useBoardActivity(boardId: string, enabled: boolean) {
  return useQuery({
    queryKey: boardActivityKey(boardId),
    queryFn: () =>
      api.get<{ activity: ActivityDto[] }>(`/boards/${boardId}/activity`).then((r) => r.activity),
    enabled,
  });
}

function useCardMutation<TInput>(
  boardId: string,
  cardId: string | null,
  request: (input: TInput) => Promise<unknown>,
  fehlermeldung: string,
) {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: request,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: boardKey(boardId) });
      if (cardId) void queryClient.invalidateQueries({ queryKey: ['card', cardId] });
    },
    onError: (error) => toast(error instanceof ApiError ? error.message : fehlermeldung, 'error'),
  });
}

export function useChecklist(boardId: string, cardId: string | null) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const add = useCardMutation<string>(
    boardId,
    cardId,
    (text) => api.post(`/cards/${cardId}/checklist`, { text }),
    'Punkt konnte nicht angelegt werden',
  );

  /**
   * Abhaken muss sofort sichtbar sein. Ohne optimistisches Update springt das
   * Haekchen erst zurueck, wenn der Server geantwortet hat — auf dem
   * Smartphone fuehlt sich das nach einem kaputten Schalter an (SPEC 16).
   */
  const toggle = useMutation({
    mutationFn: ({ id, isDone }: { id: string; isDone: boolean }) =>
      api.patch(`/checklist/${id}`, { isDone }),

    onMutate: async ({ id, isDone }) => {
      const key = ['card', cardId ?? ''];
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<{
        card: CardDto;
        checklist: ChecklistItemDto[];
      }>(key);

      queryClient.setQueryData(key, (current: typeof previous) =>
        current
          ? {
              ...current,
              checklist: current.checklist.map((item) =>
                item.id === id ? { ...item, isDone } : item,
              ),
            }
          : current,
      );
      return { previous, key };
    },

    onError: (error, _input, context) => {
      if (context?.previous) queryClient.setQueryData(context.key, context.previous);
      toast(error instanceof ApiError ? error.message : 'Änderung fehlgeschlagen', 'error');
    },

    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: boardKey(boardId) });
      if (cardId) void queryClient.invalidateQueries({ queryKey: ['card', cardId] });
    },
  });
  const remove = useCardMutation<string>(
    boardId,
    cardId,
    (id) => api.delete(`/checklist/${id}`),
    'Löschen fehlgeschlagen',
  );
  return { add, toggle, remove };
}

export function useCardLabels(boardId: string, cardId: string | null) {
  const assign = useCardMutation<{ labelId: string; assign: boolean }>(
    boardId,
    cardId,
    ({ labelId, assign: shouldAssign }) =>
      shouldAssign
        ? api.post(`/cards/${cardId}/labels`, { labelId })
        : api.delete(`/cards/${cardId}/labels/${labelId}`),
    'Label konnte nicht gesetzt werden',
  );
  return assign;
}

export function useCreateLabel(boardId: string) {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (input: { name: string; color: LabelColor }) =>
      api.post<{ label: LabelDto }>(`/boards/${boardId}/labels`, input).then((r) => r.label),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: boardKey(boardId) }),
    onError: (error) =>
      toast(error instanceof ApiError ? error.message : 'Label konnte nicht angelegt werden', 'error'),
  });
}

export function usePatchColumn(boardId: string) {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (input: {
      columnId: string;
      patch: { title?: string; wipLimit?: number | null; isDoneColumn?: boolean };
      updatedAt: string;
    }) => api.patch(`/columns/${input.columnId}`, { ...input.patch, updatedAt: input.updatedAt }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: boardKey(boardId) }),
    onError: (error) => {
      if (error instanceof ApiError && error.status === 409) {
        toast('Die Spalte wurde gerade von jemand anderem geändert. Stand aktualisiert.', 'info');
        void queryClient.invalidateQueries({ queryKey: boardKey(boardId) });
        return;
      }
      toast(error instanceof ApiError ? error.message : 'Speichern fehlgeschlagen', 'error');
    },
  });
}

export function useArchiveColumn(boardId: string) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const restore = useRestoreColumn(boardId);

  return useMutation({
    mutationFn: (columnId: string) => api.delete(`/columns/${columnId}`),
    // Optimistisch wie beim Archivieren einer Karte: auf die Serverantwort zu
    // warten liesse die Spalte samt Karten noch sichtbar stehen, obwohl das
    // Sheet schon zu ist.
    onMutate: async (columnId) => {
      await queryClient.cancelQueries({ queryKey: boardKey(boardId) });
      const previous = queryClient.getQueryData<BoardDto>(boardKey(boardId));
      const title = previous?.columns.find((column) => column.id === columnId)?.title;
      queryClient.setQueryData<BoardDto>(boardKey(boardId), (board) =>
        patchBoardCache(board, (columns) => columns.filter((column) => column.id !== columnId)),
      );
      return { previous, title };
    },
    onSuccess: (_data, columnId, context) => {
      toast(context?.title ? `Spalte „${context.title}" entfernt` : 'Spalte entfernt', 'info', {
        label: 'Rückgängig',
        onAction: () => restore.mutate(columnId),
      });
    },
    onError: (error, _columnId, context) => {
      queryClient.setQueryData(boardKey(boardId), context?.previous);
      toast(error instanceof ApiError ? error.message : 'Entfernen fehlgeschlagen', 'error');
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: boardKey(boardId) });
      void queryClient.invalidateQueries({ queryKey: archiveKey(boardId) });
    },
  });
}

/** Archivansicht des Boards (SPEC 9.6, minimal). */
export function useBoardArchive(boardId: string, enabled: boolean) {
  return useQuery({
    queryKey: archiveKey(boardId),
    queryFn: () => api.get<{ archive: BoardArchiveDto }>(`/boards/${boardId}/archive`),
    select: (data) => data.archive,
    enabled,
  });
}

const remindersKey = (cardId: string) => ['card', cardId, 'erinnerungen'] as const;

/**
 * Erinnerungen einer Karte (SPEC 10.1).
 *
 * Der Vorlauf zaehlt vor der Faelligkeit, nicht ab jetzt — deshalb aendert
 * sich der tatsaechliche Zeitpunkt mit, wenn die Karte verschoben wird. Der
 * Server rechnet das nach; hier wird nach jeder Aenderung an der Faelligkeit
 * neu geladen.
 */
export function useReminders(cardId: string | null) {
  return useQuery({
    queryKey: remindersKey(cardId ?? ''),
    queryFn: () =>
      api.get<{ reminders: ReminderDto[] }>(`/cards/${cardId}/reminders`).then((r) => r.reminders),
    enabled: Boolean(cardId),
  });
}

export function useReminderMutations(cardId: string) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: remindersKey(cardId) });

  const add = useMutation({
    mutationFn: (offsetMinutes: number) =>
      api.post(`/cards/${cardId}/reminders`, { offsetMinutes }),
    onSuccess: invalidate,
    onError: (error) =>
      toast(
        error instanceof ApiError ? error.message : 'Erinnerung konnte nicht gesetzt werden',
        'error',
      ),
  });

  const remove = useMutation({
    mutationFn: (reminderId: string) => api.delete(`/reminders/${reminderId}`),
    onSuccess: invalidate,
    onError: () => toast('Erinnerung konnte nicht entfernt werden', 'error'),
  });

  return { add, remove };
}

/** Schnellerfassung (SPEC 9.5) — der Rohtext geht zum Server, nicht das Ergebnis. */
export function useQuickCard(boardId: string) {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (input: { columnId: string; text: string }) =>
      api.post<{ card: CardDto; unresolvedAssignees: string[] }>(
        `/boards/${boardId}/quick-card`,
        input,
      ),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: boardKey(boardId) });
      // Ein unbekannter Name kostet nicht die Karte, darf aber auch nicht
      // stillschweigend verschwinden — sonst wartet jemand auf eine Aufgabe,
      // die ihm nie zugewiesen wurde.
      if (result.unresolvedAssignees.length > 0) {
        toast(`Unbekannt: ${result.unresolvedAssignees.map((n) => `@${n}`).join(', ')}`, 'info');
      }
    },
    onError: (error) =>
      toast(
        error instanceof ApiError ? error.message : 'Karte konnte nicht angelegt werden',
        'error',
      ),
  });
}

const recurrenceKey = (cardId: string) => ['card', cardId, 'wiederholung'] as const;

/** Wiederholung einer Karte setzen oder entfernen (SPEC 9.4). */
export function useRecurrence(boardId: string, cardId: string) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['card', cardId] });
    void queryClient.invalidateQueries({ queryKey: recurrenceKey(cardId) });
    void queryClient.invalidateQueries({ queryKey: boardKey(boardId) });
  };

  const save = useMutation({
    mutationFn: (input: RecurrenceInput) => api.put(`/cards/${cardId}/recurrence`, input),
    onSuccess: invalidate,
    onError: (error) =>
      toast(
        error instanceof ApiError ? error.message : 'Wiederholung konnte nicht gesetzt werden',
        'error',
      ),
  });

  const remove = useMutation({
    mutationFn: () => api.delete(`/cards/${cardId}/recurrence`),
    onSuccess: invalidate,
    onError: () => toast('Wiederholung konnte nicht entfernt werden', 'error'),
  });

  return { save, remove };
}

const viewsKey = ['views'] as const;

/** Gespeicherte Ansichten (SPEC 9.3). */
export function useSavedViews() {
  return useQuery({
    queryKey: viewsKey,
    queryFn: () => api.get<{ views: SavedViewDto[] }>('/views').then((r) => r.views),
  });
}

export function useSavedViewMutations() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: viewsKey });

  const create = useMutation({
    mutationFn: (input: { name: string; query: string; boardId?: string | null }) =>
      api.post<{ view: SavedViewDto }>('/views', input),
    onSuccess: () => {
      invalidate();
      toast('Ansicht gespeichert', 'success');
    },
    onError: () => toast('Ansicht konnte nicht gespeichert werden', 'error'),
  });

  const remove = useMutation({
    mutationFn: (viewId: string) => api.delete(`/views/${viewId}`),
    onSuccess: invalidate,
    onError: () => toast('Ansicht konnte nicht entfernt werden', 'error'),
  });

  return { create, remove };
}

/**
 * Eine ueberfaellige Karte anmahnen (Erinnern-Knopf).
 *
 * Die Antwort wird bewusst ausgesprochen statt still geschluckt: wer anmahnt,
 * soll erfahren, ob ueberhaupt jemand etwas gemerkt hat — und bei einer
 * Sperre, ab wann es wieder geht.
 */
export function useNudge(cardId: string) {
  const toast = useToast();

  return useMutation({
    mutationFn: () => api.post<NudgeResult>(`/cards/${cardId}/nudge`),
    onSuccess: (result) => {
      if (result.nudged === 0) {
        toast('Niemanden zu erinnern — die Aufgabe hängt nur an dir', 'info');
        return;
      }
      toast(
        result.delivered > 0
          ? `${result.nudged === 1 ? 'Eine Person' : `${result.nudged} Personen`} erinnert`
          : 'Erinnert — erreicht wird sie erst, wenn ein Gerät angemeldet ist',
        result.delivered > 0 ? 'success' : 'info',
      );
    },
    onError: (error) => {
      // 429: die Sperre. Der Zeitpunkt steht im Fehlerkoerper.
      if (error instanceof ApiError && error.status === 429) {
        const next = error.body.nextPossibleAt;
        toast(
          next
            ? `Schon erinnert · wieder möglich ab ${formatTime(next)}`
            : 'Diese Aufgabe wurde gerade erst angemahnt',
          'info',
        );
        return;
      }
      toast(error instanceof ApiError ? error.message : 'Erinnern fehlgeschlagen', 'error');
    },
  });
}
